import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { ReactComponent as Betterflies3 } from '../../../../assets/icons/v3/betterflies-3.svg';
import { ReactComponent as PasswordHide } from '../../../../assets/icons/v3/password-hide.svg';
import { ReactComponent as PasswordShow } from '../../../../assets/icons/v3/password-show.svg';
import { ReactComponent as TrashNew } from '../../../../assets/icons/v3/trash-new.svg';
import BdsInput from '../../../../shared/components/v3/BdsInput/BdsInput';
import BdsButton from '../../../../shared/components/v3/Buttons/BdsButton/BdsButton';
import BdsTextButton from '../../../../shared/components/v3/Buttons/BdsTextButton/BdsTextButton';
import { ARROW_LEFT_ICON } from '../../../../shared/constants/images';
import { usePasswordValidation } from '../../../../shared/hooks/usePasswordValidation';
import { TRANSLATIONS_AUTH } from '../../../../shared/translations/auth';
import PasswordValidations from '../../Login/sections/PasswordValidations/PasswordValidations';

import './CreatePassword.scss';

interface ICreatePassword {
  verificationCode?: string;
  email?: string;
  loading?: boolean;
  onSubmit: (userPassword: string) => void;
  handleGoBack: (newStep: number) => void;
  customError?: string;
  resetCustomError?: () => void;
}

const CreatePassword = ({
  email,
  verificationCode,
  loading = false,
  onSubmit,
  handleGoBack,
  customError,
  resetCustomError
}: ICreatePassword) => {
  const [userPassword, setUserPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showValidations, setShowValidations] = useState(false);
  const [fadeOut, setFadeOut] = useState(false);
  const buttonText = verificationCode
    ? TRANSLATIONS_AUTH.createPass.updateButton
    : TRANSLATIONS_AUTH.createPass.continueButton;
  const { t } = useTranslation();

  const validation = usePasswordValidation({
    password: userPassword,
    confirmPassword: userPassword
  });

  const validationTexts = () => {
    return {
      description: TRANSLATIONS_AUTH.createPass.passwordValidations.description,
      firstCondition: TRANSLATIONS_AUTH.createPass.passwordValidations.firstCondition,
      secondCondition: TRANSLATIONS_AUTH.createPass.passwordValidations.secondCondition,
      thirdCondition: TRANSLATIONS_AUTH.createPass.passwordValidations.thirdCondition
    };
  };

  const handleChangePassword = (password: string) => {
    if (customError && resetCustomError) {
      resetCustomError();
    }
    setUserPassword(password);
  };

  const handleKeyPress = () => {
    if (validation.isValidPassword()) {
      handleSubmit();
    }
  };

  const handleEyeClick = () => {
    setShowPassword(!showPassword);
  };

  const handleTrashClick = () => {
    handleGoBack(0);
  };

  const handleSubmit = () => {
    onSubmit(userPassword);
  };

  const validateCustomError = useCallback(
    (value: string | null): string | null => {
      if (!value) return null;
      if (customError) {
        return t(customError);
      }
      return null;
    },
    [customError, t]
  );

  useEffect(() => {
    const shouldShowValidations = userPassword && !validation.isValidPassword();

    if (shouldShowValidations) {
      setShowValidations(true);
      setFadeOut(false);
    } else if (showValidations && !shouldShowValidations) {
      setFadeOut(true);
      const timer = setTimeout(() => {
        setShowValidations(false);
        setFadeOut(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [userPassword, validation, showValidations]);

  return (
    <div className="auth-card-container" data-experience="new">
      <div className="auth-card-go-back">
        <BdsTextButton action={() => handleGoBack(0)} iconLeft={ARROW_LEFT_ICON}>
          {t(TRANSLATIONS_AUTH.goBack)}
        </BdsTextButton>
      </div>
      <div className="cp-betterfly-image">
        <Betterflies3 className="image" />
      </div>
      {verificationCode && (
        <div className="verification-code-content">
          <span className="verification-code-text">{verificationCode}</span>
          <TrashNew className="trash-icon" aria-label="Trash Icon" onClick={handleTrashClick} />
        </div>
      )}
      <div className="create-password-container flex flex-col gap-11">
        <div className="flex flex-col gap-6">
          <div className="cp-title">{t(TRANSLATIONS_AUTH.createPass.title)}</div>
          {email && <div className="cp-description">{email}</div>}
        </div>
        <div className="flex flex-col gap-6">
          <BdsInput
            hintText={t(TRANSLATIONS_AUTH.createPass.placeholder)}
            onChanged={handleChangePassword}
            onSubmitPressed={handleKeyPress}
            enabled={true}
            type={showPassword ? 'text' : 'password'}
            onSuffixIconClick={handleEyeClick}
            suffixIcon={showPassword ? <PasswordHide /> : <PasswordShow />}
            validator={validateCustomError}
            autoComplete="new-password"
          />
          {showValidations && (
            <div className={`cp-password-validations ${fadeOut ? 'fade-out' : ''}`}>
              <PasswordValidations validation={validation} texts={validationTexts()} />
            </div>
          )}
        </div>
        {!showValidations && (
          <div className={`cp-password-validations ${fadeOut ? 'fade-out' : ''}`}>
            <BdsButton
              variant="primary"
              size="l"
              action={handleSubmit}
              disabled={!validation.isValidPassword() || loading}
              loading={loading}
            >
              {t(buttonText)}
            </BdsButton>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePassword;
